import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { MessagesService, ReferenceHttpService } from 'shared/domain';
import { ReferencePopoverComponent } from 'shared/feature-reference-popover';
import {
  ConfirmationDialogComponent,
  DialogService,
  IconDirective,
  PopoverService,
  ButtonDirective,
  ReferenceCardComponent,
  ConfirmationDialogArgs,
} from 'shared/ui-common';
import { isTruthy } from 'shared/util-common';

import { Reference, ReferencePopoverData, ReferenceWithNumber } from '@treetop/shared/model';
import { ReferenceNumberingService } from '@treetop/shared/reference-numbering';

@Component({
  standalone: true,
  imports: [TranslocoModule, ReferenceCardComponent, MatIconModule, IconDirective, ButtonDirective],
  selector: 'guideline-sidebar-references',
  templateUrl: './sidebar-references.component.html',
  styleUrls: ['./sidebar-references.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarReferencesComponent {
  private referenceNumberingService = inject(ReferenceNumberingService);
  protected dialog = inject(MatDialog);
  private popoverService = inject(PopoverService);
  private referenceHttpService = inject(ReferenceHttpService);
  private messageService = inject(MessagesService);
  private dialogService = inject(DialogService);

  public references = input<Reference[]>([]);

  @Output() public referencesChanged = new EventEmitter<Reference[]>();

  protected referencesWithNumbers = computed(() => {
    return this.referenceNumberingService.orderedReferences().filter((reference) =>
      this.references()
        ?.map((ref) => ref?.id)
        .includes(reference.id),
    );
  });

  protected deleteReference(referenceToDelete: ReferenceWithNumber) {
    const args: ConfirmationDialogArgs = {
      message: 'DeleteLiterature',
      component: referenceToDelete.title,
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Delete',
    };

    const deleteDialog = this.dialogService.open(ConfirmationDialogComponent, {
      icon: 'circle-x',
      title: 'DeleteLiterature',
      data: args,
      disableClose: true,
    });

    deleteDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.referencesChanged.emit(this.references().filter((reference) => referenceToDelete.id !== reference.id));
      }
    });
  }

  protected addReference(event: MouseEvent) {
    this.popoverService
      .open<ReferencePopoverData, Reference[]>(ReferencePopoverComponent, event.currentTarget as HTMLElement, {
        positionStrategy: 'bottom-right',
        data: {
          selected: this.references(),
          disabled: this.references(),
        },
      })
      .afterClosed()
      .pipe(
        isTruthy(),
        switchMap((references) => {
          const newReferences = references.filter((reference) => /^[0-]+$/.test(reference.id));
          const existingReferences = references.filter((reference) => !newReferences.includes(reference));
          return this.handleAddNewReferences(newReferences).pipe(map((result) => [...result, ...existingReferences]));
        }),
      )
      .subscribe((references) => {
        this.referencesChanged.emit(references);
      });
  }

  public handleAddNewReferences(references: Reference[]): Observable<Reference[]> {
    return this.referenceHttpService.addReferences(references).pipe(
      catchError(() => {
        this.messageService.showError('SaveError');

        return of([] as Reference[]);
      }),
    );
  }

  protected editReference(reference: ReferenceWithNumber) {
    window.open(`/manage/literature?id=${reference.id}`, '_blank');
  }
}
